/* eslint-disable max-lines -- no plans to split this file */
import { inject, Injectable } from '@angular/core';
import { mapAsKeys } from '@sama/js-core';
import { isEmpty } from '@sama/micro-dash';
import { forEach, pickBy } from 'lodash';
import { AbstractContentConnector } from '../cross-team-shared/abstract-content-connector';
import { AnnotationTransientState } from '../cross-team-shared/annotation/shared/state/annotation-state';
import {
  OrthographicShapeState3D,
  ShapeState2D,
  ShapeState3D,
} from '../cross-team-shared/annotation/shared/state/shape-state';
import {
  EventTrackingValueWithSource,
  truncateToPoint2D,
} from '../cross-team-shared/misc-utils';
import { ShapeFilter } from '../cross-team-shared/state/abstract-sensor-content-state';
import {
  MenuType,
  PointCloudContentConnector as PointCloudContentConnectorInterface,
  ShapeGroupOrderState,
} from '../team-apis/content/content-connector';
import { ViewBox } from '../team-apis/content/view-box';
import { EventTrackingValue } from '../team-apis/core/event-tracking';
import { IN_PROGRESS_INDEX } from '../team-apis/core/reserved-shape-indexes';
import { PointCloudShapeType } from '../team-apis/otterdam/answers/point-cloud-answer';
import {
  Point2D,
  Point3D,
  PointType,
} from '../team-apis/otterdam/answers/shape-typings';
import { Task } from '../team-apis/otterdam/task';
import { ContextMenuOptionsService } from './misc-services/context-menu-options.service';
import { ProjectionService } from './misc-services/projection.service';
import { UnprojectionService } from './misc-services/unprojection.service';
import { MainCameraService } from './render/cameras/main-camera.service';
import { SideViewCamerasService } from './render/cameras/side-view-cameras.service';
import { MissingShapeMarkerService } from './render/mesh/missing-shape-marker/missing-shape-marker-service';
import { LineService } from './render/mesh/poly-shape/line/line.service';
import { PolygonService } from './render/mesh/poly-shape/polygon/polygon.service';
import { CuboidLocationState3D } from './state/cuboid-location-state-3d';
import { CuboidState3D, isCuboidState3D } from './state/cuboid-state-3d';
import { isLineState3D } from './state/line-state-3d';
import { PointCloudToolName } from './state/point-cloud-content-state';
import { PointCloudContentStateUndoService } from './state/point-cloud-content-state-undo.service';
import { PointCloudContentStore } from './state/point-cloud-content-store';
import { isPolygonState3D } from './state/polygon-state-3d';
import { ShapeLocationState3D } from './state/shape-location-state-3d';
import { LoadTaskDataService } from './task-data/load-task-data.service';
import { InspectService } from './toolbars/inspect.service';
import { ToolManager3DService } from './tools/tool-manager-3d.service';
import { KdTreeService } from './utils/kd-tree.service';
import { PointCloudAssetService } from './utils/loading/point-cloud-asset.service';
import { MouseCoordinateService } from './utils/mouse-coordinate.service';
import {
  ensureKeyLocation,
  interpolateLocation,
} from './utils/point-cloud-interpolation-helper';
import { midPoint, subtractPoints } from './utils/point-utils';

@Injectable()
export class PointCloudContentConnector
  extends AbstractContentConnector<
    Point3D,
    PointCloudShapeType,
    ShapeLocationState3D
  >
  implements PointCloudContentConnectorInterface
{
  // Used in type guards, make sure they are updated if modifying this line
  isPointCloudContentConnector = true;

  protected store = inject(PointCloudContentStore);
  protected pointCloudAssetService = inject(PointCloudAssetService);

  private contextMenuOptionsService = inject(ContextMenuOptionsService);
  private inspectService = inject(InspectService);
  private linesService = inject(LineService);
  private loadTaskDataService = inject(LoadTaskDataService);
  private missingShapeMarkerService = inject(MissingShapeMarkerService);
  private polygonService = inject(PolygonService);
  private projectionService = inject(ProjectionService);
  private unprojectionService = inject(UnprojectionService);
  private undoService = inject(PointCloudContentStateUndoService);
  private kdTreeService = inject(KdTreeService);
  private mainCameraService = inject(MainCameraService);
  private toolManagerService = inject(ToolManager3DService);
  private mouseCoordinateService = inject(MouseCoordinateService);
  private sideViewsCameraService = inject(SideViewCamerasService);

  canRedo() {
    return this.undoService.canRedo();
  }

  canUndo() {
    return this.undoService.canUndo();
  }

  determineLocation(frameNumber: number, shapeIndex: number) {
    return interpolateLocation(
      this.store.state().render.cuboids[shapeIndex].key_locations,
      frameNumber,
    );
  }

  deactivateSelectedTool() {
    const selectedTool = this.toolManagerService.getSelectedTool();
    if (selectedTool?.activated$.value) {
      selectedTool.onDeactivate();
    }
  }

  async activateSelectedTool() {
    const selectedTool = this.toolManagerService.getSelectedTool();
    if (selectedTool && !selectedTool.activated$.value) {
      await this.toolManagerService.getSelectedTool()?.onActivate();
    }
  }

  ensureKeyLocation(
    shapeIndex: number,
    frameNumber: number,
    specifiedLocation?: CuboidLocationState3D,
  ) {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- fix me!
    if (this.store.state().render.cuboids[shapeIndex]) {
      ensureKeyLocation(
        this.store('render')('cuboids')(shapeIndex)('key_locations'),
        frameNumber,
        specifiedLocation,
      );
    }
  }

  async getContextMenuOptions(menuType: MenuType) {
    return this.contextMenuOptionsService.getContextMenuOptions(menuType);
  }

  groundPoint(point: PointType): Point3D {
    const { searchRadius, useAllPoints } =
      this.store.state().render.layerSettings.groundingConfiguration;
    const zValue = this.kdTreeService.getGroundZ(
      truncateToPoint2D(point),
      searchRadius,
      useAllPoints ? 'all' : 'ground',
    );
    return [point[0], point[1], zValue];
  }

  inspectFeedbackMarker(position: Point3D) {
    return this.inspectService.inspectPosition(position);
  }

  override async inspectFocusedShape(_maintainZoom?: boolean) {
    return this.inspectService.inspectFocusedShape();
  }

  override isOutputBlank() {
    return isEmpty(this.store.state().render.cuboids);
  }

  override wasShapeCancelled(): boolean {
    throw new Error('Method not implemented.');
  }

  loadTask(task: Task) {
    this.store.assign({ task, taskExtras: task._extras });
    this.loadTaskDataService.loadTaskData();
    return this.pointCloudAssetService.loadTask();
  }

  buildProjectedCuboid(
    videoOutputName: string,
    points: Point2D[],
    originShape: ShapeState2D,
  ) {
    return this.unprojectionService.buildProjectedCuboid(
      videoOutputName,
      points,
      originShape,
    );
  }

  getProjectedRectanglePoints(cuboidIndex: number, videoOutputName: string) {
    return this.projectionService.getProjectedRectanglePoints(
      cuboidIndex,
      videoOutputName,
    );
  }

  pushCurrentState(
    options: {
      collectKey?: string;
      collectDebounce?: number;
    } = {},
  ) {
    return this.undoService.pushCurrentState(options);
  }

  redo() {
    this.undoService.redo();
  }

  resetSensorView(eventTrackingValueWithSource: EventTrackingValueWithSource) {
    this.mainCameraService.reset(eventTrackingValueWithSource);
    this.sideViewsCameraService.resetZoomForAllCameras();
  }

  override setFeedbackMarkers(marker: Point3D[]) {
    this.missingShapeMarkerService.setMissingShapeMarker(marker);
  }

  override setFrameNumber(frameNumber: number) {
    this.store('render')('frameNumber').set(frameNumber);
  }

  setShapeFilters(filters: ShapeFilter[]) {
    this.store('render')('shapeFilters').set(filters);
  }

  setShapeTransientInfo(
    shapeTransientInfo: StrictRecord<number, AnnotationTransientState>,
  ) {
    this.store('render')('shapeTransientInfo').set(shapeTransientInfo);
  }

  setShapes({
    shapes,
    groups,
    shapeAndGroupOrder,
  }: ShapeGroupOrderState<PointCloudShapeType, Point3D, ShapeState3D>) {
    // All shapes are added to core store, but only cuboids should be set
    // using setShapes
    this.store.batch(() => {
      // @ts-ignore -- fix me
      this.store('render')('cuboids').set(pickBy(shapes, isCuboidState3D));
      if (this.store.state().project.use_annotation_store) {
        // @ts-ignore -- fix me
        this.store('render')('lines').set(pickBy(shapes, isLineState3D));
        // @ts-ignore -- fix me
        this.store('render')('polygons').set(pickBy(shapes, isPolygonState3D));
      }
      // @ts-ignore -- fix me
      this.store('groups').set(groups);
      this.store('shapeAndGroupOrder').set(shapeAndGroupOrder);
    });
  }

  showTrainingShapes(shapes: ShapeState3D[]) {
    this.store('render')('trainingShapes').assign({
      cuboids: this.convertToTrainingShapes(shapes.filter(isCuboidState3D)),
      lines: this.convertToTrainingShapes(shapes.filter(isLineState3D)),
      polygons: this.convertToTrainingShapes(shapes.filter(isPolygonState3D)),
    });
  }

  setOrthographicShape(currentShape: OrthographicShapeState3D) {
    if (isLineState3D(currentShape)) {
      this.linesService.setShape(currentShape);
    } else if (isPolygonState3D(currentShape)) {
      this.polygonService.setShape(currentShape);
    }
  }

  setEditPanelLocation(location: 'left' | 'bottom') {
    this.store('editPanel')('location').set(location);
  }

  deleteOrthographicShape(indexes: number[]): void {
    forEach(indexes, (index) => {
      this.store.batch(() => this.store('render')('lines')(index).delete());
      this.store.batch(() => this.store('render')('polygons')(index).delete());
    });
  }

  setSelection(indexes: number[]) {
    const { cuboids, lines, polygons } = this.store.state().render;
    this.store('render')('selection').set(
      mapAsKeys(
        indexes.filter(
          (index) =>
            index === IN_PROGRESS_INDEX ||
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- need to update type to strictRecord one day
            cuboids[index] ||
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- need to update type to strictRecord one day
            lines[index] ||
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- need to update type to strictRecord one day
            polygons[index],
        ),
        () => true,
      ),
    );
  }

  reset() {
    this.undoService.reset();
  }

  undo() {
    this.undoService.undo();
  }

  handleViewBoxUpdate(outputName: string, viewbox: ViewBox) {
    this.store('viewBoxes')(outputName).set(viewbox);
  }

  handleZoomPanelViewBoxUpdate(outputName: string, viewbox: ViewBox) {
    this.store('zoomPanelViewBoxes')(outputName).set(viewbox);
  }

  validateShape(shape: CuboidState3D) {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- fix me!
    if (shape.type !== 'cuboid') {
      return `${shape.type}  is not a valid shape type`;
    }
    return undefined;
  }

  setSplitViewOutputName(outputName: string | undefined) {
    this.store('splitViewOutputName').set(outputName);
  }

  override setTool(toolName: PointCloudToolName, mode?: string) {
    this.store('selectedTool').set(toolName);

    if (mode) {
      this.toolManagerService.getTool(toolName).setMode(mode);
    }
  }

  override cancelTool(eventTrackingValue: EventTrackingValue) {
    this.toolManagerService.cancelTool(eventTrackingValue);
  }

  offsetShapesToMousePosition(shapes: CuboidState3D[]) {
    const shapeCenters = shapes.map((shape) => shape.key_locations[0].position);
    const centerOfAllShapes = midPoint(shapeCenters);

    const newCenter = this.mouseCoordinateService.getGroundedMousePosition();
    const deltaBetweenCenters = subtractPoints(centerOfAllShapes, newCenter);

    return shapes.map((shape) => {
      const { position, scale } = shape.key_locations[0];
      const newPosition = subtractPoints(position, deltaBetweenCenters);
      const groundZ = this.kdTreeService.getGroundZ(
        truncateToPoint2D(newPosition),
      );
      newPosition[2] = groundZ + scale[2] / 2;
      shape.key_locations[0].position = newPosition;
      return shape;
    });
  }
}
